<template>
  <div :class="['pickup-store-card', { active: hasInfo }]">
    <vue-client-only>
      <div
        class="pickup-store-card__head"
        @click="$emit('back')"
      >
        <f-sprite-icon :icon-link="$options.icons.IconArrow.id" />
        <template v-if="!adaptive.isDesktop">
          Все пункты самовывоза
        </template>
        <template v-else>
          Назад
        </template>
        <div
          v-if="!adaptive.isDesktop"
          class="close-button"
          @click="$emit('close')"
        >
          <f-sprite-icon :icon-link="$options.icons.IconClose.id" />
        </div>
      </div>
    </vue-client-only>

    <div class="pickup-store-card__body">
      <div class="pickup-store-card__info">
        <checkout-delivery-widget
          v-if="hasInfo"
          :place="WIDGET_PLACES.storeCard"
          class="pickup-store-card__delivery-widget"
        />

        <template v-if="store.type === 'store'">
          <div class="pickup-store-card__info-store-title">
            <div class="pickup-store-card__info-store">
              <vue-client-only>
                <template v-if="!adaptive.isMobile">
                  Магазин
                </template>
              </vue-client-only>
              <f-sprite-icon :icon-link="$options.icons.IconLogo.id" />
            </div>

            <products-count-tag
              :count="store.availableLinesCount"
              :total="ids.length"
            />
          </div>
        </template>
        <template v-else>
          <div class="pickup-store-card__info-alt-title">
            <div>
              <template v-if="store.type === 'postamat'">
                <f-sprite-icon :icon-link="$options.icons.IconTerminal.id" />
              </template>
              <template v-else>
                <f-sprite-icon :icon-link="$options.icons.IconPickup.id" />
              </template>

              <span>{{ store.name }}</span>
            </div>
            <span>
              {{ store.availableLinesCount }} {{ store.availableLinesCount | decl('товар', ['', 'а', 'ов']) }} из {{ ids.length }}
            </span>
          </div>
        </template>

        <div
          v-if="hasMetros"
          class="pickup-store-card__info-metro"
        >
          <div
            v-for="metro in store.metro"
            :key="metro.id"
          >
            <span :style="{ backgroundColor: metro.line.color }" />{{ metro.name }}
          </div>
        </div>

        <div class="pickup-store-card__info-title">
          <span class="pickup-store-card__info-address">{{ store.address }}</span>
          <span v-if="distance" class="pickup-store-card__info-distance">{{ distance }}</span>
        </div>

        <template v-if="store.workTime">
          <div
            v-if="computedPhone"
            class="pickup-store-card__info-phone"
          >
            <span>Телефон:</span>
            {{ store.phone | noBracesPhone }}
          </div>
          <div class="pickup-store-card__info-schedule">
            <span>Часы работы:</span> {{ store | parseWorkSchedule }}
          </div>
        </template>

        <hint-block
          v-if="store.type !== 'store' && store.drivingDirections"
          class="pickup-store-card__info-hint"
          title="Как добраться"
          :description="store.drivingDirections"
          :secondary="store.insideLocation"
        />

        <div
          v-if="checkLabels(store)"
          class="pickup-store-card__info-labels"
        >
          <tag
            v-if="store.needPrepay"
            title="100%&nbsp;&nbsp;предоплата"
            width="120px"
            color="#ffb71b"
          />
          <tag
            v-if="checkQrCodeRequirement(store)"
            title="Вход по QR"
            width="80px"
            color="#C1E0E0"
          />
        </div>

        <ui-notification
          v-if="isLockdownMode && onlineOrdersOnly"
          class="pickup-store-card__info-warning"
          color="#FFB71B"
        >
          Магазин работает в формате выдачи онлайн-заказов
        </ui-notification>


        <div class="pickup-store-card__bottom">
          <div class="pickup-store-card__bottom-markers">
            <div v-if="hasFitting">
              <div class="pickup-store-card__bottom-icon">
                <f-sprite-icon :icon-link="$options.icons.IconFitting.id" />
              </div>
              Возможна примерка
            </div>
            <div v-if="hasCardPay">
              <div class="pickup-store-card__bottom-icon">
                <f-sprite-icon :icon-link="$options.icons.IconCard.id" />
              </div>
              Оплата картой
            </div>
            <div v-if="hasRefund">
              <div class="pickup-store-card__bottom-icon">
                <f-sprite-icon :icon-link="$options.icons.IconReturn.id" />
              </div>
              Срок возврата 14 дней
            </div>
          </div>

          <ui-button
            wide
            class="pickup-store-card__bottom-button"
            text="Выбрать"
            @click="$emit('select')"
          />
        </div>
      </div>

      <div class="pickup-store-card__products">
        <div class="pickup-store-card__products-title">
          Товары, доступные для самовывоза
        </div>

        <ui-loader
          v-if="!availableProducts.length"
          class="pickup-store-card__products-list-loader"
          size="60"
        />
        <div
          v-else
          class="pickup-store-card__products-list"
        >
          <store-card-product
            v-for="(product, i) in availableProducts"
            :key="`${product.skuId}:${i}`"
            :product="product"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import VueClientOnly from 'vue-client-only';
import { LOCKDOWN_MODE } from '@/consts';
import { WIDGET_PLACES } from '@/components/checkout/checkout-delivery-widget/constants';

/* Components */
import StoreCardProduct from '@/components/checkout/delivery-selector/store-card-product';
import HintBlock from '@kit/hint-block/hint-block';
import Tag from '@kit/tag/tag.vue';
import ProductsCountTag from '@/components/checkout/delivery-selector/products-count-tag.vue';
import CheckoutDeliveryWidget from '@/components/checkout/checkout-delivery-widget/checkout-delivery-widget.vue';

/* Icons */
import IconArrow from '@icons/arrow.svg';
import IconLogo from '@images/logo.multi.svg';
import IconCard from '@icons/cardpay.svg';
import IconClose from '@icons/close.svg';
import IconFitting from '@icons/fitting.svg';
import IconReturn from '@icons/return.svg'
import IconPickup from '@icons/pvz.svg';
import IconTerminal from '@icons/postamat.svg';

export default {
  name: 'pickup-store-card',

  components: {
    ProductsCountTag,
    CheckoutDeliveryWidget,
    HintBlock,
    StoreCardProduct,
    VueClientOnly,
    Tag
  },

  inject: ['adaptive'],

  icons: {
    IconArrow,
    IconLogo,
    IconCard,
    IconClose,
    IconFitting,
    IconPickup,
    IconTerminal,
    IconReturn,
  },

  props: {
    store: {
      type: Object,
      default: () => {}
    },

    products: {
      type: Array,
      default: () => []
    },

    ids: {
      type: Array,
      default: () => []
    },

    distance: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      availableProducts: [],
    }
  },

  computed: {
    WIDGET_PLACES: () => WIDGET_PLACES,

    hasInfo() {
      return !!Object.keys(this.store).length;
    },

    typeIsStore() {
      return this.store.type === 'store';
    },

    hasFitting() {
      return this.typeIsStore || this.store?.services?.includes('has_fitting');
    },

    hasRefund() {
      return this.typeIsStore || this.store?.services?.includes('has_refund');
    },

    hasCardPay() {
      return this.typeIsStore
          || this.store?.services?.includes('cash_and_card')
          || this.store?.services?.includes('card_only');
    },

    hasMetros() {
      return this.store?.metro?.length;
    },

    computedPhone() {
      return this.store?.phone;
    },

    onlineOrdersOnly() { // параметр на периоды локдауна
      return this.store.type === 'store'
          && (this.store.workMode && this.store.workMode !== 'Магазин');
    },

    isLockdownMode() {
      return LOCKDOWN_MODE;
    }
  },

  watch: {
    store() {
      if (this.hasInfo) this.checkCartAvailability();
    }
  },

  methods: {
    checkCartAvailability() {
      return this.$store.$api.checkCartAvailability({
        type: this.store.type,
        storeId: this.store.id,
        lineIds: this.ids,
      }).then(availableProductsInfo => {
        this.availableProducts = availableProductsInfo.map((availableProductInfo) => {
          const product = this.products.find(({skuId}) => skuId === availableProductInfo.skuId) || {};

          return {
            ...product,
            ...availableProductInfo
          }
        });
      });
    },

    checkLabels(store = {}) {
      return store.needPrepay || this.checkQrCodeRequirement(store);
    },

    checkQrCodeRequirement(store) {
      const { workMode } = store;
      return !!(workMode && workMode.toLowerCase() !== 'магазин');
    },
  }
};
</script>

<style lang="scss">
.pickup-store-card {
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  z-index: 10;
  width: 100%;
  background-color: #fff;
  transition: .3s transform ease;
  overflow-y: auto;
  transform: translateX(-100%);

  &.active {
    transform: translateX(0);
  }

  &__head {
    display: flex;
    align-items: center;
    margin-top: 12px;
    margin-left: 12px;
    font-size: 14px;
    font-weight: 600;
    line-height: 24px;
    color: $blue;

    & > svg {
      width: 24px;
      height: 24px;
      margin-right: 12px;
      transform: rotate(-90deg);
    }
  }

  &__body {
    margin-top: 22px;
    padding: 0 16px 43px;
  }

  &__info {
    padding: 24px;
    border-radius: 4px;
    border: 1px solid $gray_lu;

    &-store-title,
    &-alt-title {
      display: flex;
      justify-content: space-between;

      & > div {
        display: flex;
        align-items: center;

        span {
          font-size: 14px;
          line-height: 24px;
        }
      }
    }

    &-store-title {
      align-items: center;

      & > div {
        display: flex;
        align-items: center;

        & > svg {
          height: 12px;
          width: 83px;
        }
      }
    }

    &-alt-title {
      align-items: flex-start;

      & > div {
        display: inline;
        margin-right: 8px;
        line-height: 22px;

        & > svg {
          float: left;
          width: 20px;
          height: 20px;
          margin-right: 8px;
          color: $gray;
        }
      }
    }

    &-metro {
      display: flex;
      flex-wrap: wrap;
      margin-top: 12px;
      font-size: 14px;
      line-height: 24px;
    }

    &-metro > div {
      display: flex;
      align-items: center;
      margin-right: 12px;

      & > span {
        display: block;
        width: 8px;
        height: 8px;
        margin-right: 8px;
        border-radius: 50%;
      }
    }

    &-labels {
      margin-top: 4px;

      .tag {
        display: inline-block;
        margin-left: 4px;

        &:first-child {
          margin-left: 0;
        }
      }
    }

    &-hint {
      margin-top: 4px;
      margin-bottom: 8px;
    }

    &-warning {
      margin-top: 8px;
      margin-bottom: 16px;
      padding: 12px 12px 12px 8px;

      .ui-notification-subtitle {
        font-size: 12px;
        line-height: 16px;
        color: $black;
      }
    }

    &-phone {
      margin-top: 12px;
    }

    &-phone,
    &-schedule {
      font-size: 14px;
      line-height: 24px;

      & > span {
        color: $gray;
      }
    }

    &-schedule {
      margin-top: 4px;
    }
  }

  &__info-title {
    display: flex;
    margin-top: 12px;
  }

  &__info-address {
    flex-grow: 1;
    font-size: 14px;
    font-weight: 600;
    line-height: 20px;
  }

  &__info-distance {
    flex-shrink: 0;
    margin-left: 10px;
    text-align: right;
    font-size: 12px;
    line-height: 20px;
    font-weight: 400;
    color: $gray;
    white-space: nowrap;
    opacity: .5;
  }

  &__bottom {
    margin-top: 11px;

    &-markers {
      margin-top: 12px;
    }

    &-markers > div {
      display: flex;
      align-items: center;
      margin-top: 13px;
      font-size: 14px;
      line-height: 24px;

      &:first-child {
        margin-top: 0;
      }
    }

    &-icon {
      display: flex;
      justify-content: center;
      align-items: center;
      width: 33px;
      height: 33px;
      margin-right: 4px;
      color: $gray;
      background-color: $gray_bgr;
      border-radius: 50%;
    }

    &-icon > svg {
      width: 20px;
      height: 20px;
    }

    &-button {
      margin-top: 24px;
    }
  }

  &__products {
    margin-top: 16px;

    &-title {
      font-size: 16px;
      font-weight: 600;
      line-height: 24px;
    }

    &-list-loader {
      position: relative;
      margin: 100px auto;
      color: $blue;
    }

    &-list {
      margin-top: 14px;
      height: 200px;
    }
  }
}

@include breakpoint("tablet") {
  .pickup-store-card {
    &__delivery-widget {
      margin-bottom: 20px;
    }

    &__body {
      margin-top: 24px;
      padding: 0 32px;
    }

    &__info {
      padding: 20px 24px 24px;

      &-store {
        font-size: 14px;
        line-height: 24px;
      }

      &-store > svg {
        margin-left: 6px;
      }

      &-store > span {
        margin-left: auto;
      }

      &-labels {
        margin-top: 12px;
      }

      &-warning {
        padding: 12px 20px 12px 15px;
        margin-bottom: 0;
      }
    }

    &__bottom {
      display: flex;
      align-items: flex-end;
      justify-content: space-between;
      margin-top: 0;
      padding: 16px 0 3px;

      &-markers {
        width: 65%;
        display: flex;
        flex-direction: column;
        margin-top: 0;
      }

      &-markers > div {
        margin: 0 10px 7px 0;
      }

      &-markers > div:last-child {
        margin-bottom: 0;
      }

      &-icon {
        margin-right: 8px;
      }

      &-button {
        width: 200px;
        height: 40px;
        margin-top: 0;
        font-size: 16px;
        line-height: 24px;
      }
    }

    &__products {
      margin-top: 24px;
    }
  }
}

@include breakpoint("desktop") {
  .pickup-store-card {
    position: absolute;
    width: 597px;
    box-shadow: 0 0 10px rgba(0, 0, 0, .25);
    overflow-y: visible;

    &__head {
      margin-left: 26px;
      margin-top: 29px;
      font-size: 24px;
      font-weight: 400;
      color: $gray;
      cursor: pointer;
    }

    &__body {
      display: flex;
      flex-direction: column;
      height: calc(100% - 67px);
      margin-top: 14px;
      padding: 0 0 0 37px;
    }

    &__info {
      padding: 0 17px 0 0;
      border: none;

      &-metro {
        margin-top: 8px;
      }

      &-schedule {
        margin-top: 4px;
      }
    }

    &__info-title {
      margin-top: 8px;
    }

    &__info-address {
      flex-grow: 0;
    }

    &__info-distance {
      flex-grow: 1;
      flex-basis: 163px;
      margin-left: 4px;
    }

    &__buttom {
      padding: 16px 0 24px;

      &-button {
        width: 170px;
        height: 40px;
      }
    }

    &__products {
      border-top: 1px solid $gray_lu;
      padding-top: 15px;
      overflow-y: auto;
      flex: 1;

      &-title {
        font-size: 20px;
        line-height: 28px;
      }

      &-list::after {
        content: '';
        position: sticky;
        left: 0;
        bottom: -1px;
        display: block;
        width: 100%;
        height: 12px;
        background-color: #fff;
      }
    }
  }
}
</style>
